// Data-channel transport tuning (webrtc-transfer-tuning, Phase 1).
//
// Every constant that shapes a large transfer over the WebRTC data channel
// lives here, so the high/low-water *pair* is chosen rather than inherited:
// before this module the low mark was simple-peer's 64 KB default and the
// high mark a function-local const in PeerBinary.sendBig.
//
//   highWater        sendBig stops pushing chunks when bufferedAmount exceeds this
//   lowWater         the channel's bufferedAmountLowThreshold — sendBig resumes
//                    on the 'bufferedamountlow' event this fires
//   drainTimeout     max ms a single backpressure wait may stall (0 = no limit)
//   sendTimeout      max ms one sendBig payload may take end to end, drain waits
//                    included (0 = no limit)
//   streamThreshold  bodies past this size go as peerRequestStream /
//                    peerResponseStream messages instead of one msgpack payload
//   iceTransportPolicy  RTCPeerConnection policy ('all' | 'relay'); 'relay'
//                    forces TURN so measurements can be taken on the relayed path
//
// Defaults (webrtc-transfer-tuning Phase 3, 2026-09-08 — 44 measured runs
// on relayed and direct cross-network paths plus loopback):
//   highWater 1 MB      — unchanged; no pair beat it outside noise
//   lowWater 512 KB     — was simple-peer's inherited 64 KB. The 64 KB
//                          floor let a fast pipe run dry (loopback idle 50 %,
//                          echo 15–25 %); 512 KB cost nothing in any sweep
//   streamThreshold 1 MB — was 8 MB. The stream path is free at the
//                          boundary on every path, while an under-gate body
//                          rides one msgpack payload under a single 30 s
//                          sendTimeout: 8 MB at the 64 KB/s seen in July
//                          could never finish; 1 MB does in 16 s
//   drainTimeout / sendTimeout 30 s — unchanged

export const DEFAULT_TRANSPORT = Object.freeze({
    highWater: 1 * 1024 * 1024,
    lowWater: 512 * 1024,
    drainTimeout: 30000,
    sendTimeout: 30000,
    streamThreshold: 1 * 1024 * 1024,
    iceTransportPolicy: undefined,
})

const NUMERIC = ['highWater', 'lowWater', 'drainTimeout', 'sendTimeout', 'streamThreshold']
const POLICIES = new Set(['all', 'relay', undefined])

/**
 * Merge transport overrides onto the defaults, later layers winning, and
 * validate the result. Unknown keys are ignored; `undefined` leaves the
 * lower layer's value in place.
 * @param {...(object|undefined)} layers
 * @returns {Readonly<typeof DEFAULT_TRANSPORT>}
 */
export function resolveTransport(...layers) {
    const out = { ...DEFAULT_TRANSPORT }
    for (const layer of layers) {
        if (!layer || typeof layer !== 'object') continue
        for (const key of Object.keys(DEFAULT_TRANSPORT)) {
            if (layer[key] !== undefined) out[key] = layer[key]
        }
    }
    for (const key of NUMERIC) {
        const v = out[key]
        if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) {
            throw new TypeError(`transport.${key} must be a non-negative finite number, got ${v}`)
        }
    }
    if (out.lowWater >= out.highWater) {
        throw new RangeError(
            `transport.lowWater (${out.lowWater}) must be below transport.highWater (${out.highWater})`
        )
    }
    if (!POLICIES.has(out.iceTransportPolicy)) {
        throw new RangeError(`transport.iceTransportPolicy must be 'all' or 'relay', got ${out.iceTransportPolicy}`)
    }
    return Object.freeze(out)
}
